import BaseEntityModel from '@/models/core/BaseEntityModel';
import type { EmployeeResponse } from '@/types/employee';
import type { EmployeeWithRoleResponse } from '@/types/employee';
import type { RoleResponse } from '@/types/role';
import { parseISO } from 'date-fns';
import Roles from '@/constants/Roles';

class EmployeeModel extends BaseEntityModel {
  email: string | null;
  firstName: string;
  lastActivity: Date | null;
  lastName: string;
  pin: string | null;
  roleUid: string | null;
  userUid: string | null;
  deletedAt: Date | null;
  role: RoleResponse | null;

  constructor(model: Partial<EmployeeResponse | EmployeeWithRoleResponse> = {}) {
    super(model);
    this.email = model.email ?? null;
    this.firstName = model.first_name ?? '';
    this.lastActivity = model.last_activity !== null ? parseISO(model.last_activity ?? '') : null;
    this.lastName = model.last_name ?? '';
    this.pin = model.pin ?? null;
    this.roleUid = model.role_uid ?? null;
    this.userUid = model.user_uid ?? null;
    this.deletedAt = model.deleted_at !== null ? parseISO(model.deleted_at ?? '') : null;

    // Handle role information from join query
    if ('role_name' in model && 'role_slug' in model && model.role_name && model.role_slug) {
      this.role = {
        created_at: '',
        name: model.role_name,
        slug: model.role_slug,
        uid: this.roleUid || '',
      };
    } else {
      this.role = null;
    }
  }

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get isDeleted(): boolean {
    return this.deletedAt !== null;
  }

  /**
   * Check if the employee has admin role
   */
  get isAdmin(): boolean {
    return this.role?.slug === Roles.Admin;
  }

  /**
   * Check if the employee has user role
   */
  get isUser(): boolean {
    return this.role?.slug === Roles.User;
  }

  /**
   * Check if the employee has employee role
   */
  get isEmployee(): boolean {
    return this.role?.slug === Roles.Employee;
  }

  /**
   * Check if the employee has admin or user role (privileged roles)
   */
  get isAdminOrUser(): boolean {
    return this.isAdmin || this.isUser;
  }

  /**
   * Check if the employee has a specific role
   */
  hasRole(roleSlug: string): boolean {
    return this.role?.slug === roleSlug;
  }
}

export default EmployeeModel;
